import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Keyboard } from 'lucide-react';

interface Shortcut {
  keys: string[];
  description: string;
}

export function KeyboardShortcutsHelp() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === '?') {
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT')) {
          return;
        }
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  if (!open) return null;

  const shortcuts: { category: string; items: Shortcut[] }[] = [
    {
      category: t('shortcuts.general'),
      items: [
        { keys: ['Ctrl', 'K'], description: t('shortcuts.commandPalette') },
        { keys: ['Shift', '?'], description: t('shortcuts.showShortcuts') },
      ],
    },
    {
      category: t('shortcuts.navigation'),
      items: [
        { keys: ['G', 'D'], description: t('shortcuts.goToDashboard') },
        { keys: ['G', 'P'], description: t('shortcuts.goToProjects') },
        { keys: ['G', 'T'], description: t('shortcuts.goToTasks') },
        { keys: ['G', 'N'], description: t('shortcuts.goToNotifications') },
      ],
    },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-blue-500" />
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">{t('shortcuts.title')}</h2>
          </div>
          <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[60vh] overflow-y-auto">
          {shortcuts.map(({ category, items }) => (
            <div key={category}>
              <h3 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">{category}</h3>
              <div className="space-y-2">
                {items.map((shortcut, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{shortcut.description}</span>
                    <div className="flex items-center gap-1">
                      {shortcut.keys.map((key, j) => (
                        <span key={j}>
                          <kbd className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded shadow-sm">
                            {key}
                          </kbd>
                          {j < shortcut.keys.length - 1 && <span className="text-gray-300 dark:text-gray-600 mx-0.5">+</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 text-center">
          <p className="text-xs text-gray-400">{t('shortcuts.pressEscToClose')}</p>
        </div>
      </div>
    </div>
  );
}
